import React, { ReactNode } from 'react'
import { Box, Typography } from '@mui/material'

// Helper types for read-only fields
export interface ReadOnlyBaseProps {
  label: string
  fullWidth?: boolean
}

// Read-only field for radio buttons
export interface ReadOnlyRadioFieldProps extends ReadOnlyBaseProps {
  value: string | null | undefined
  options: { value: string, label: string }[]
}

export const ReadOnlyRadioField: React.FC<ReadOnlyRadioFieldProps> = ({ label, value, options, fullWidth = true }) => {
  // Find the matching option label
  const selectedOption = options.find(opt => opt.value === value);
  const displayValue = selectedOption ? selectedOption.label : (value || '-');
  
  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto', mb: 0.75 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
        {label}
      </Typography>
      <Box
        sx={{
          p: 1,
          minHeight: '36px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {value && (
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                mr: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'common.white',
                }}
              />
            </Box>
          )}
          <Typography variant="body1">{displayValue}</Typography>
        </Box>
      </Box>
    </Box>
  )
}

// Read-only field for select/dropdown fields
export interface ReadOnlySelectFieldProps extends ReadOnlyBaseProps {
  value: string | null | undefined
  options?: { value: string, label: string }[] | string[]
}

export const ReadOnlySelectField: React.FC<ReadOnlySelectFieldProps> = ({ label, value, options = [], fullWidth = true }) => {
  // Handle both array of strings and array of objects
  let displayValue = value || '-';
  
  if (options.length > 0) {
    if (typeof options[0] === 'string') {
      // If options is an array of strings, just display the value
      displayValue = value || '-';
    } else if (typeof options[0] === 'object') {
      // If options is an array of objects, find the matching label
      const selectedOption = (options as { value: string, label: string }[]).find(opt => opt.value === value);
      displayValue = selectedOption ? selectedOption.label : (value || '-');
    }
  }
  
  return (
    <Box sx={{ width: fullWidth ? '100%' : 'auto', mb: 0.75 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
        {label}
      </Typography>
      <Box
        sx={{
          p: 1,
          minHeight: '36px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography variant="body1">{displayValue}</Typography>
      </Box>
    </Box>
  )
}

// Helper function to render appropriate read-only field based on field type
export const renderReadOnlyField = (
  fieldType: 'text' | 'select' | 'radio' | 'checkbox' | 'custom' | 'money',
  label: string,
  value: any,
  options?: any[],
  customRender?: ReactNode
) => {
  switch (fieldType) {
    case 'radio':
      return (
        <ReadOnlyRadioField
          label={label}
          value={value}
          options={options || []}
        />
      );
    case 'select':
      return (
        <ReadOnlySelectField
          label={label}
          value={value}
          options={options}
        />
      );
    case 'checkbox':
      return (
        <Box sx={{ width: '100%', mb: 0.75 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
            {label}
          </Typography>
          <Box sx={{ p: 1, minHeight: '36px', display: 'flex', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  border: '1px solid',
                  borderColor: 'primary.main',
                  borderRadius: 0.5,
                  mr: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: value ? 'primary.main' : 'transparent',
                }}
              >
                {value && (
                  <Typography
                    sx={{
                      color: 'common.white',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      lineHeight: 1,
                    }}
                  >
                    ✓
                  </Typography>
                )}
              </Box>
              <Typography>{value ? 'Yes' : 'No'}</Typography>
            </Box>
          </Box>
        </Box>
      );
    case 'money':
      return (
        <Box sx={{ width: '100%', mb: 0.75 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
            {label}
          </Typography>
          <Box sx={{ p: 1, minHeight: '36px', display: 'flex', alignItems: 'center' }}>
            <Typography variant="body1">
              {value ? `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
            </Typography>
          </Box>
        </Box>
      );
    case 'custom':
      return (
        <Box sx={{ width: '100%', mb: 0.75 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
            {label}
          </Typography>
          <Box sx={{ p: 1, minHeight: '36px', display: 'flex', alignItems: 'center' }}>
            {customRender || <Typography variant="body2" color="text.secondary">-</Typography>}
          </Box>
        </Box>
      );
    case 'text':
    default:
      return (
        <Box sx={{ width: '100%', mb: 0.75 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
            {label}
          </Typography>
          <Box sx={{ p: 1, minHeight: '36px', display: 'flex', alignItems: 'center' }}>
            {typeof value === 'string' || typeof value === 'number' ? (
              <Typography variant="body1">{value || '-'}</Typography>
            ) : (
              value || <Typography variant="body2" color="text.secondary">-</Typography>
            )}
          </Box>
        </Box>
      );
  }
}